import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface NewProduct {
  id: string;
  product_id: string;
  bc: string | null;
  product_naam: string | null;
  eenheid: string | null;
  locatie: string | null;
  voorraad: number;
  moq: number | null;
  vrije_waarde_2: number | null;
  first_seen_at: string;
  hidden: boolean;
  hidden_at: string | null;
  hidden_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface NewProductsFilters {
  search: string;
  dateFilter: "all" | "today" | "week" | "month";
  showHidden: boolean;
}

export interface NewProductsStats {
  total: number;
  today: number;
  thisWeek: number;
  hidden: number;
}

export function useNewProducts(filters: NewProductsFilters) {
  return useQuery({
    queryKey: ["new-products", filters],
    queryFn: async () => {
      let query = supabase
        .from("new_products_mag2")
        .select("*")
        .order("first_seen_at", { ascending: false });

      // Apply hidden filter
      if (!filters.showHidden) {
        query = query.eq("hidden", false);
      }

      // Apply date filter
      const now = new Date();
      if (filters.dateFilter === "today") {
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        query = query.gte("first_seen_at", todayStart);
      } else if (filters.dateFilter === "week") {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        query = query.gte("first_seen_at", weekAgo);
      } else if (filters.dateFilter === "month") {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        query = query.gte("first_seen_at", monthAgo);
      }

      // Apply search filter
      if (filters.search) {
        const searchTerm = `%${filters.search}%`;
        query = query.or(
          `product_naam.ilike.${searchTerm},product_id.ilike.${searchTerm},bc.ilike.${searchTerm}`
        );
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch new products: ${error.message}`);
      }

      return data as NewProduct[];
    },
  });
}

export function useNewProductsStats() {
  return useQuery({
    queryKey: ["new-products-stats"],
    queryFn: async () => {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

      // Get all counts in parallel
      const [totalResult, todayResult, weekResult, hiddenResult] = await Promise.all([
        supabase.from("new_products_mag2").select("id", { count: "exact", head: true }).eq("hidden", false),
        supabase.from("new_products_mag2").select("id", { count: "exact", head: true }).eq("hidden", false).gte("first_seen_at", todayStart),
        supabase.from("new_products_mag2").select("id", { count: "exact", head: true }).eq("hidden", false).gte("first_seen_at", weekAgo),
        supabase.from("new_products_mag2").select("id", { count: "exact", head: true }).eq("hidden", true),
      ]);

      return {
        total: totalResult.count || 0,
        today: todayResult.count || 0,
        thisWeek: weekResult.count || 0,
        hidden: hiddenResult.count || 0,
      } as NewProductsStats;
    },
  });
}

export function useHideProducts() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (productIds: string[]) => {
      const { error } = await supabase
        .from("new_products_mag2")
        .update({
          hidden: true,
          hidden_at: new Date().toISOString(),
        })
        .in("id", productIds);

      if (error) {
        throw new Error(`Failed to hide products: ${error.message}`);
      }

      return productIds.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["new-products"] });
      queryClient.invalidateQueries({ queryKey: ["new-products-stats"] });
      toast({
        title: "Producten verborgen",
        description: `${count} product(en) zijn gemarkeerd als niet meer tonen.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Fout",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUnhideProducts() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (productIds: string[]) => {
      const { error } = await supabase
        .from("new_products_mag2")
        .update({
          hidden: false,
          hidden_at: null,
          hidden_by: null,
        })
        .in("id", productIds);

      if (error) {
        throw new Error(`Failed to unhide products: ${error.message}`);
      }

      return productIds.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["new-products"] });
      queryClient.invalidateQueries({ queryKey: ["new-products-stats"] });
      toast({
        title: "Producten hersteld",
        description: `${count} product(en) zijn weer zichtbaar.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Fout",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
